import React, { useRef, useCallback, useState, useEffect } from 'react';
import { Mic, X, Check, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { LiveWaveform } from '@/components/ui/live-waveform';
import { useAudioDevices } from '@/hooks/useAudioDevices';
import {
  CHATBOT_CHAT_MSJ_FROM_BOT,
  CHATBOT_ERROR_ACCESSING_MEDIA_DEVICES,
  CHATBOT_ERROR_MESSAGE_AUDIO,
  CHATBOT_ERROR_TRANSCRIBING_AUDIO
} from '@/utils/constants';

const CHATBOT_UTN_MINE_TYPE_AUDIO = 'audio/webm; codecs="opus"';

// Function to get the best available audio MIME type
const getBestAudioMimeType = (): string => {
  const types = [
    'audio/webm; codecs="opus"',
    'audio/webm',
    'audio/mp4',
    'audio/ogg; codecs="opus"',
    'audio/wav'
  ];
  
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return 'audio/webm'; // fallback
};

interface AudioRecorderProps {
  isLoading: boolean;
  isRecording: boolean;
  onRecordingChange: (recording: boolean) => void;
  onSendAudio: (audio: string) => void;
  onShowMessage: (from: string, message: string) => void;
}

export function AudioRecorder({
  isLoading,
  isRecording,
  onRecordingChange,
  onSendAudio,
  onShowMessage,
}: AudioRecorderProps) {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { devices, selectedDeviceId, setSelectedDeviceId, getDevices } = useAudioDevices();
  const [showSettings, setShowSettings] = useState(false);
  
  // Silence detection refs
  const hasAudioActivityRef = useRef(false);
  const isCancelledRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const silenceDetectionIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  const cleanupSilenceDetection = useCallback(() => {
    if (silenceDetectionIntervalRef.current) {
      clearInterval(silenceDetectionIntervalRef.current);
      silenceDetectionIntervalRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  // Handle stream ready from LiveWaveform
  const handleStreamReady = useCallback((stream: MediaStream) => {
    try {
      // Reset flags
      hasAudioActivityRef.current = false;
      isCancelledRef.current = false;

      // Setup silence detection
      const AudioContextConstructor = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextConstructor();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      sourceRef.current = source;

      // Check for audio activity every 100ms
      silenceDetectionIntervalRef.current = setInterval(() => {
        if (!analyserRef.current) return;
        
        const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Calculate average volume
        const sum = dataArray.reduce((a, b) => a + b, 0);
        const average = sum / dataArray.length;
        
        // Threshold for activity (same logic logic as old component ~2% volume)
        // Old logic: normalizedVolume > 2 (where max is 100)
        // Here average is 0-255. 2/100 * 255 ≈ 5.
        if (average > 5) {
          hasAudioActivityRef.current = true;
        }
      }, 100);

      const bestMimeType = getBestAudioMimeType();
      const mediaRecorder = new MediaRecorder(stream, {
        audioBitsPerSecond: 320000,
        mimeType: bestMimeType,
      });

      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop silence detection
        cleanupSilenceDetection();

        if (isCancelledRef.current) {
           return;
        }
        
        if (chunksRef.current.length === 0) {
           // Probably cancelled or error
           return;
        }

        // Check audio activity
        if (!hasAudioActivityRef.current) {
          onShowMessage(CHATBOT_CHAT_MSJ_FROM_BOT, CHATBOT_ERROR_MESSAGE_AUDIO);
          return;
        }

        const blob = new Blob(chunksRef.current, { type: CHATBOT_UTN_MINE_TYPE_AUDIO });

        try {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = function() {
            const base64Audio = String(reader.result).split(',')[1];
            onSendAudio(base64Audio);
          };
          
          reader.onerror = function() {
            console.error('Audio processing error: Failed to read file');
            onShowMessage(CHATBOT_CHAT_MSJ_FROM_BOT, CHATBOT_ERROR_TRANSCRIBING_AUDIO);
          };
        } catch (error) {
          console.error('Audio processing error:', error);
          onShowMessage(CHATBOT_CHAT_MSJ_FROM_BOT, CHATBOT_ERROR_TRANSCRIBING_AUDIO);
        }
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;

      // Set a timeout to stop recording after 3 minutes
      recordingTimeoutRef.current = setTimeout(() => {
        handleStopRecording();
      }, 180000);
    } catch (err) {
      console.error(CHATBOT_ERROR_ACCESSING_MEDIA_DEVICES, err);
      onShowMessage(CHATBOT_CHAT_MSJ_FROM_BOT, CHATBOT_ERROR_ACCESSING_MEDIA_DEVICES);
      onRecordingChange(false);
      cleanupSilenceDetection();
    }
  }, [onSendAudio, onShowMessage, onRecordingChange, cleanupSilenceDetection]);

  // Handle stream end
  const handleStreamEnd = useCallback(() => {
    if (recordingTimeoutRef.current) {
      clearTimeout(recordingTimeoutRef.current);
    }
    cleanupSilenceDetection();
  }, [cleanupSilenceDetection]);

  // Handle error
  const handleError = useCallback((error: Error) => {
    console.error('Microphone error:', error);
    onShowMessage(CHATBOT_CHAT_MSJ_FROM_BOT, CHATBOT_ERROR_ACCESSING_MEDIA_DEVICES);
    onRecordingChange(false);
    cleanupSilenceDetection();
  }, [onShowMessage, onRecordingChange, cleanupSilenceDetection]);

  // Start recording
  const handleStartRecording = useCallback(() => {
    if (isLoading) return;
    setShowSettings(false);
    onRecordingChange(true);
  }, [isLoading, onRecordingChange]);

  // Stop recording
  const handleStopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    onRecordingChange(false);
  }, [onRecordingChange]);

  // Cancel recording
  const handleCancelRecording = useCallback(() => {
    isCancelledRef.current = true;
    chunksRef.current = [];
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    onRecordingChange(false);
  }, [onRecordingChange]);

  const toggleSettings = () => {
    if (!showSettings) {
      getDevices();
    }
    setShowSettings(!showSettings);
  };

  const handleDeviceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDeviceId(e.target.value);
    // Optionally close settings after selection? No, user might want to see.
  };

  return (
    <div className="relative flex items-center gap-2">
      {/* Recording State - Live Waveform */}
      {isRecording && (
        <>
          <div className="flex items-center gap-2 h-10 px-3 bg-muted rounded-lg border border-border flex-1 min-w-[200px]">
            {/* Recording indicator */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
              <span className="text-xs font-medium text-foreground whitespace-nowrap">Grabando</span>
            </div>
            
            {/* Live Waveform */}
            <div className="flex-1 min-w-0">
              <LiveWaveform
                active={isRecording}
                deviceId={selectedDeviceId}
                height={32}
                barWidth={2}
                barGap={1}
                barRadius={1}
                mode="static"
                fadeEdges={true}
                historySize={60}
                onStreamReady={handleStreamReady}
                onStreamEnd={handleStreamEnd}
                onError={handleError}
              />
            </div>
            
            {/* Cancel button */}
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={handleCancelRecording}
              className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 flex-shrink-0"
              aria-label="Cancelar grabación"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Send button - visible when recording */}
          <Button
            type="button"
            size="icon"
            onClick={handleStopRecording}
            className="h-10 w-10 flex-shrink-0"
            aria-label="Enviar audio"
          >
            <Check className="h-5 w-5" />
          </Button>
        </>
      )}

      {/* Microphone Button - Not Recording State */}
      {!isRecording && (
        <div className="flex items-center gap-1">
          {/* Settings Button */}
          <div className="relative">
            <Button
              type="button"
              size="icon"
              variant="ghost"
              disabled={isLoading}
              onClick={toggleSettings}
              className={cn(
                "h-10 w-8 rounded-lg transition-colors text-gray-500",
                showSettings && "bg-gray-100 text-gray-900"
              )}
              aria-label="Configurar micrófono"
            >
              <Settings className="h-4 w-4" />
            </Button>

            {/* Device Selector Popup */}
            {showSettings && (
              <>
                <div 
                  className="fixed inset-0 z-[9998]" 
                  onClick={() => setShowSettings(false)}
                />
                <div className="absolute bottom-full mb-2 right-0 z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 p-2 min-w-[200px] max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200 origin-bottom-right">
                  <div className="text-xs font-medium text-gray-500 mb-1.5 px-1">Micrófono</div>
                  <select
                    value={selectedDeviceId}
                    onChange={handleDeviceChange}
                    className="w-full text-sm p-1.5 rounded border border-gray-300 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 bg-white text-gray-900"
                  >
                    {devices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>

        <Button
          type="button"
          size="icon"
          variant="ghost"
          disabled={isLoading}
          onClick={handleStartRecording}
          className={cn(
            "h-10 w-10 rounded-lg transition-colors",
            isLoading 
              ? "text-gray-400 cursor-not-allowed" 
              : "text-gray-600 hover:text-primary hover:bg-gray-100"
          )}
          aria-label="Grabar audio"
        >
          <Mic className="h-5 w-5" />
        </Button>
        </div>
      )}
    </div>
  );
}
